import React from "react";
import { motion } from "framer-motion";
import { X, Power, CheckCircle2, AlertCircle } from "lucide-react";

const ActivationConfirmationModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  promoterName = "",
  isActivating = false,
  isActive = false
}) => {
  if (!isOpen) return null;

  const action = isActive ? "Deactivate" : "Activate";
  const actionLower = action.toLowerCase();

  return (
    <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="mx-4 w-full max-w-md bg-white rounded-2xl shadow-xl"
      >
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div className="flex gap-3 items-center">
            <div className={`p-2 rounded-lg ${
              isActive ? "bg-red-100" : "bg-green-100"
            }`}>
              <Power className={`w-5 h-5 ${
                isActive ? "text-red-600" : "text-green-600"
              }`} />
            </div>
            <h2 className="text-xl font-bold text-gray-900">{action} Publisher</h2>
          </div>
          <button
            onClick={onClose}
            disabled={isActivating}
            className="p-2 text-gray-400 rounded-lg transition-colors hover:text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-4 p-4 bg-green-50 rounded-lg border border-green-200">
            <div className="flex gap-3 items-start">
              <CheckCircle2 className="w-5 h-5 mt-0.5 text-green-600 flex-shrink-0" />
              <div>
                <p className="font-semibold text-green-900 mb-1">
                  Ready to Activate
                </p>
                <p className="text-sm text-green-800">
                  {promoterName ? (
                    <>
                      <strong>{promoterName}</strong> has been approved and is ready to be activated.
                    </>
                  ) : (
                    "This publisher is ready to be activated."
                  )}
                </p>
              </div>
            </div>
          </div>

          <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex gap-3 items-start">
              <AlertCircle className="w-5 h-5 mt-0.5 text-blue-600 flex-shrink-0" />
              <div>
                <p className="font-semibold text-blue-900 mb-2">
                  What happens after activation?
                </p>
                <ul className="space-y-1 text-sm text-blue-800">
                  <li>• Publisher can start promoting live campaigns</li>
                  <li>• Publisher becomes eligible to earn commissions</li>
                  <li>• Publisher gains access to all active deals</li>
                  <li>• Publisher can track performance and earnings</li>
                </ul>
              </div>
            </div>
          </div>

          <p className="text-sm text-gray-600">
            Are you sure you want to activate this publisher? This action will enable them to start promoting campaigns and earning commissions.
          </p>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            disabled={isActivating}
            className="flex-1 px-4 py-2 text-gray-700 bg-white rounded-lg border border-gray-300 transition-colors hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isActivating}
            className={`flex flex-1 gap-2 justify-center items-center px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              isActive
                ? "bg-red-600 hover:bg-red-700"
                : "bg-green-600 hover:bg-green-700"
            }`}
          >
            {isActivating ? (
              <>
                <div className="w-4 h-4 rounded-full border-2 border-white animate-spin border-t-transparent"></div>
                {action}ing...
              </>
            ) : (
              <>
                <Power className="w-4 h-4" />
                {action} Publisher
              </>
            )}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default ActivationConfirmationModal;
